import React, { useState, useEffect } from "react";
import "./BookmarkCountdown.scss";

/**
 * Alternatywny komponent odliczający czas do podanej daty.
 * W normalnym widoku pokazuje pełny timer, a po przypięciu (scroll) pokazuje mały bookmark.
 * Po najechaniu na bookmark w trybie przypiętym rozwija się pełny timer.
 *
 * @param {string}  title       - Tytuł/napis wyświetlany nad timerem.
 * @param {string}  targetDate  - Data docelowa w formacie ISO, np. "2026-12-31T23:59:59".
 * @param {string}  expiredText - Tekst wyświetlany po zakończeniu odliczania (opcjonalny).
 * @param {"en"|"pl"} lang        - Język etykiet: "en" (domyślnie) lub "pl".
 * @param {"h1"|"h2"|"h3"|"h4"|"h5"|"h6"} titleTag - Tag HTML tytułu (domyślnie "h2").
 * @param {"black"|"white"} textColorVariant - Wariant koloru tekstu.
 * @param {boolean} isPinned - Jeśli true, komponent pokazuje bookmark i rozwija timer na hover.
 */

const ALLOWED_TITLE_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6"];
const TEXT_COLOR_VARIANTS = {
  black: "#00414b",
  white: "#ffffff",
};
const UNIT_LABELS = {
  en: [
    { one: "Day", other: "Days" },
    { one: "Hour", other: "Hours" },
    { one: "Minute", other: "Minutes" },
    { one: "Second", other: "Seconds" },
  ],
  pl: [
    { one: "Dzień", other: "Dni" },
    { one: "Godzina", other: "Godziny" },
    { one: "Minuta", other: "Minuty" },
    { one: "Sekunda", other: "Sekundy" },
  ],
};

function calculateTimeLeft(targetDate) {
  const diff = Math.floor((new Date(targetDate) - new Date()) / 1000);
  if (diff <= 0) return null;

  return [
    Math.floor(diff / 86400),
    Math.floor((diff % 86400) / 3600),
    Math.floor((diff % 3600) / 60),
    diff % 60,
  ];
}

function pad(value) {
  return String(value).padStart(2, "0");
}

const BookmarkCountdown = ({
  title = "Do wydarzenia pozostało:",
  targetDate = "2027-01-01T00:00:00",
  expiredText = "Wydarzenie już się odbyło!",
  lang = "en",
  titleTag: TitleTag = "h2",
  textColorVariant = "black",
  isPinned = false,
}) => {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetDate));
  const [isHovered, setIsHovered] = useState(false);

  const labelsConfig = UNIT_LABELS[lang] || UNIT_LABELS.en;
  const unitLabels = timeLeft
    ? labelsConfig.map((unit, index) => (timeLeft[index] === 1 ? unit.one : unit.other))
    : [];
  const daysLabel = timeLeft && timeLeft[0] === 1 ? labelsConfig[0].one.toLowerCase() : labelsConfig[0].other.toLowerCase();
  const SafeTitleTag = ALLOWED_TITLE_TAGS.includes(TitleTag) ? TitleTag : "h2";
  const resolvedTextColor = TEXT_COLOR_VARIANTS[textColorVariant] || TEXT_COLOR_VARIANTS.black;
  const wrapperStyle = { "--countdown-text-color": resolvedTextColor };

  useEffect(() => {
    const tick = () => setTimeLeft(calculateTimeLeft(targetDate));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const days = timeLeft ? pad(timeLeft[0]) : "0";
  const showFullTimer = !isPinned || isHovered;
  const wrapperClassName = `bookmark-countdown-wrapper${
    isPinned ? " bookmark-countdown-wrapper--pinned" : ""
  }${isHovered ? " bookmark-countdown-wrapper--hovered" : ""}`;

  return (
    <div
      className={wrapperClassName}
      style={wrapperStyle}
      onMouseEnter={() => {
        if (isPinned) setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (isPinned) setIsHovered(false);
      }}
    >
      {isPinned && (
        <div className="bookmark-countdown-wrapper__bookmark">
          <div className="bookmark-countdown-wrapper__bookmark-content">
            <span className="bookmark-countdown-wrapper__days">{days}</span>
            <span className="bookmark-countdown-wrapper__days-label">{daysLabel}</span>
          </div>
        </div>
      )}

      {showFullTimer && (
        <div className="bookmark-countdown-wrapper__full-timer">
          {title && <SafeTitleTag className="bookmark-countdown-wrapper__title">{title}</SafeTitleTag>}

          {timeLeft === null ? (
            <p className="bookmark-countdown-wrapper__expired">{expiredText}</p>
          ) : (
            <div className="bookmark-countdown-wrapper__timer">
              {unitLabels.map((label, index) => (
                <div key={`${label}-${index}`} className="bookmark-countdown-wrapper__unit">
                  <span className="bookmark-countdown-wrapper__value">{pad(timeLeft[index])}</span>
                  <span className="bookmark-countdown-wrapper__label">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookmarkCountdown;
